// src/FleetManagement.jsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import fleetManagement from './assets/things/fleetManagement.jpg';
import { TIERS, BASIS, tierByKey, rateLabel } from './data/pricing';

const LAMBDA_URL = 'https://4ybasbtcgniwwjras22lmro55i0dawsw.lambda-url.us-east-1.on.aws/';

const included = [
  'Professional listings and dynamic pricing',
  'Guest communication and support',
  'Interior and exterior cleaning, with delivery to guests',
  'Parking and guest drop-off coordination',
  '100+ pre- and post-trip photos',
  'Damage claim management',
  'Maintenance coordination, including taking your vehicle to scheduled appointments',
];

const steps = [
  { title: 'Apply', text: 'Tell us about your vehicle: make, model, year and mileage.' },
  {
    title: 'We review it',
    text: 'We check its condition, age and mileage, confirm which class it falls in, and send you terms.',
  },
  { title: 'We run it', text: 'We list it, handle guests, clean it, deliver it and manage claims and maintenance.' },
];

const money = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function conversionEvent() {
  // Replace AW-CONVERSION_ID/CONVERSION_LABEL with the values from Google Ads
  // (Goals > Conversions) once the conversion action exists.
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'generate_lead', { event_category: 'owner_application' });
  window.gtag('event', 'conversion', { send_to: 'AW-CONVERSION_ID/CONVERSION_LABEL' });
}

function readTracking() {
  try {
    return JSON.parse(sessionStorage.getItem('pfr_tracking') || '{}');
  } catch (e) {
    return {};
  }
}

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  make: '',
  model: '',
  year: '',
  mileage: '',
  message: '',
  website: '', // honeypot: real people never see or fill this
};

export default function FleetManagement() {
  const [tierKey, setTierKey] = useState('economy');
  const [earningsInput, setEarningsInput] = useState('2000');
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const tier = tierByKey(tierKey);
  const earnings = Math.max(0, Number(earningsInput) || 0);
  const fee = Math.round((earnings * tier.rate) / 100);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const choose = (key) => {
    setTierKey(key);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.website) return; // honeypot tripped
    setError('');
    setSubmitting(true);

    const year = parseInt(form.year, 10);
    const mileage = parseInt(form.mileage, 10);
    const isEligible = mileage <= 100000 && new Date().getFullYear() - year <= 10;

    const { website, ...fields } = form;
    const payload = {
      ...fields,
      tier: tier.key,
      plan: tier.key,
      tierRate: tier.rate,
      eligible: isEligible,
      source: 'pfrlv.com/fleet-management',
      page: window.location.href,
      timestamp: new Date().toISOString(),
      ...readTracking(),
    };

    try {
      const res = await fetch(LAMBDA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      conversionEvent();
      setSubmitted(true);
    } catch (err) {
      console.error('Application failed:', err);
      setError('The application did not go through. Check your connection and try again, or email info@primefleetholdings.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ---------- Hero ---------- */}
      <section className="relative isolate">
        <img src={fleetManagement} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" />
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              'linear-gradient(100deg, rgba(14,13,11,0.97) 0%, rgba(14,13,11,0.88) 55%, rgba(14,13,11,0.6) 100%)',
          }}
        />
        <div className="container-x section-y">
          <h1 className="display max-w-3xl text-5xl sm:text-6xl">
            We manage your car on Turo. You collect the earnings.
          </h1>
          <p className="mt-6 max-w-xl text-lg" style={{ color: 'rgba(244,239,230,0.82)' }}>
            Full management on every vehicle. Our fee is a percentage of your Turo earnings, set by what kind of
            vehicle you own.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button type="button" className="btn btn-primary" onClick={() => goTo('rates')}>
              See what you would pay
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => goTo('apply')}>
              Apply now
            </button>
          </div>
        </div>
      </section>

      {/* ---------- Rates ---------- */}
      <section id="rates" className="paper section-y" style={{ scrollMarginTop: '4rem' }}>
        <div className="container-x">
          <h2 className="display text-4xl sm:text-5xl">Our fee, by vehicle class</h2>
          <p className="muted mt-4 max-w-2xl text-lg">
            The percentage is taken from {BASIS}.
          </p>

          <div className="mt-10 grid items-start gap-8 lg:grid-cols-[1.25fr_1fr] lg:gap-12">
            <div role="radiogroup" aria-label="Vehicle class" style={{ borderTop: '1px solid var(--paper-line)' }}>
              {TIERS.map((t) => {
                const active = t.key === tierKey;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => choose(t.key)}
                    className="flex w-full items-center justify-between gap-6 py-5 pl-4 pr-2 text-left transition-colors"
                    style={{
                      borderBottom: '1px solid var(--paper-line)',
                      borderLeft: `4px solid ${active ? 'var(--bronze)' : 'transparent'}`,
                      background: active ? 'rgba(194,128,58,0.12)' : 'transparent',
                    }}
                  >
                    <span>
                      <span className="block text-lg font-semibold">{t.label}</span>
                      <span className="muted block text-sm">{t.desc}</span>
                    </span>
                    <span className="display shrink-0 text-5xl">{rateLabel(t)}</span>
                  </button>
                );
              })}
            </div>

            <div className="on-dark rounded-2xl p-6 sm:p-8" style={{ background: 'var(--paper-ink)', color: 'var(--ink)' }}>
              <h3 className="display text-2xl">What that looks like</h3>
              <label htmlFor="earnings" className="field-label mt-5">
                Monthly Turo earnings, after Turo&rsquo;s fee (example)
              </label>
              <div className="relative">
                <span className="muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">$</span>
                <input
                  id="earnings"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  step="100"
                  value={earningsInput}
                  onChange={(e) => setEarningsInput(e.target.value)}
                  className="field !pl-7"
                />
              </div>

              <dl className="mt-6">
                  <div className="flex items-baseline justify-between py-3" style={{ borderTop: '1px solid var(--line)' }}>
                    <dt className="muted">Our fee ({tier.rate}%)</dt>
                    <dd className="display text-2xl">{money(fee)}</dd>
                  </div>
                  <div className="flex items-baseline justify-between py-3" style={{ borderTop: '1px solid var(--line)' }}>
                    <dt className="muted">You keep</dt>
                    <dd className="display text-3xl" style={{ color: 'var(--bronze-text)' }}>
                      {money(earnings - fee)}
                    </dd>
                  </div>
                </dl>
              <p className="muted mt-2 text-xs">
                An illustration of the fee only. Your actual earnings depend on your car, your trips and the season.
              </p>
              <button type="button" className="btn btn-primary mt-6 w-full" onClick={() => goTo('apply')}>
                Apply for management
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Included + process ---------- */}
      <section className="section-y">
        <div className="container-x grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="display text-3xl sm:text-4xl">Included in every class</h2>
            <ul className="mt-6" style={{ borderTop: '1px solid var(--line)' }}>
              {included.map((item) => (
                <li key={item} className="py-3" style={{ borderBottom: '1px solid var(--line)' }}>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="display text-3xl sm:text-4xl">How it works</h2>
            <ol className="mt-6" style={{ borderTop: '1px solid var(--line)' }}>
              {steps.map((s, i) => (
                <li
                  key={s.title}
                  className="grid grid-cols-[2.5rem_1fr] gap-2 py-4"
                  style={{ borderBottom: '1px solid var(--line)' }}
                >
                  <span className="display text-2xl" style={{ color: 'var(--bronze-text)' }}>
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold">{s.title}</h3>
                    <p className="muted">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ---------- Application ---------- */}
      <section id="apply" className="section-y" style={{ background: 'var(--surface)', scrollMarginTop: '4rem' }}>
        <div className="container-x" style={{ maxWidth: '44rem' }}>
          {submitted ? (
            <div role="status">
              <h2 className="display text-4xl">Application received</h2>
              <p className="muted mt-4 text-lg">
                Thanks. We will review your vehicle and email you the next steps.
              </p>
              <Link to="/" className="btn btn-ghost mt-8">
                Back to home
              </Link>
            </div>
          ) : (
            <>
              <h2 className="display text-4xl sm:text-5xl">Apply for management</h2>
              <p className="muted mt-4 text-lg">
                Tell us about the vehicle. We will review it, confirm its class and send you terms.
              </p>

              <form onSubmit={onSubmit} className="mt-8 grid gap-5 sm:grid-cols-2">
                {/* Honeypot */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
                  <label>
                    Website
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange} />
                  </label>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="tier" className="field-label">
                    Vehicle class
                  </label>
                  <select id="tier" value={tierKey} onChange={(e) => choose(e.target.value)} className="field">
                    {TIERS.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label} ({t.rate}%)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="name" className="field-label">Your name</label>
                  <input id="name" name="name" type="text" autoComplete="name" required value={form.name} onChange={onChange} className="field" />
                </div>
                <div>
                  <label htmlFor="email" className="field-label">Email</label>
                  <input id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={onChange} className="field" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="phone" className="field-label">Phone</label>
                  <input id="phone" name="phone" type="tel" autoComplete="tel" required value={form.phone} onChange={onChange} className="field" />
                </div>

                <div>
                  <label htmlFor="make" className="field-label">Vehicle make</label>
                  <input id="make" name="make" type="text" required value={form.make} onChange={onChange} className="field" />
                </div>
                <div>
                  <label htmlFor="model" className="field-label">Vehicle model</label>
                  <input id="model" name="model" type="text" required value={form.model} onChange={onChange} className="field" />
                </div>
                <div>
                  <label htmlFor="year" className="field-label">Year</label>
                  <input id="year" name="year" type="number" inputMode="numeric" min="1990" max={new Date().getFullYear() + 1} required value={form.year} onChange={onChange} className="field" />
                </div>
                <div>
                  <label htmlFor="mileage" className="field-label">Current mileage</label>
                  <input id="mileage" name="mileage" type="number" inputMode="numeric" min="0" required value={form.mileage} onChange={onChange} className="field" />
                </div>


                <div className="sm:col-span-2">
                  <label htmlFor="message" className="field-label">Anything else we should know? (optional)</label>
                  <textarea id="message" name="message" rows={3} value={form.message} onChange={onChange} className="field" />
                </div>

                {error && (
                  <p role="alert" className="sm:col-span-2" style={{ color: '#ff9b8a' }}>
                    {error}
                  </p>
                )}

                <div className="sm:col-span-2">
                  <button type="submit" disabled={submitting} className="btn btn-primary w-full sm:w-auto">
                    {submitting ? 'Sending...' : 'Send application'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </>
  );
}
